import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { formatEuro, formatDate, relativeDays } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Plus, Search, Pencil, Trash2, RefreshCw, AlertCircle,
  Building2, LineChart, Home, Car, Banknote, Bitcoin, Box,
} from "lucide-react";
import AssetForm, { ASSET_TYPE_LABELS } from "../components/AssetForm";
import AssetValueDialog from "../components/AssetValueDialog";
import { toast } from "sonner";

const TYPE_ICON = {
  bank: Building2,
  investment: LineChart,
  real_estate: Home,
  vehicle: Car,
  cash: Banknote,
  crypto: Bitcoin,
  other: Box,
};

const TYPE_COLOR = {
  bank: "#2C4A3B",
  investment: "#829A88",
  real_estate: "#B9762E",
  vehicle: "#C89B6A",
  cash: "#1D3328",
  crypto: "#E07A5F",
  other: "#5C635A",
};

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [valueTarget, setValueTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assets");
      setAssets(data.assets || []);
      setTotal(data.total_value || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return assets;
    const s = q.toLowerCase();
    return assets.filter((a) =>
      [a.name, a.owner, a.type, a.notes].some((f) =>
        (f || "").toLowerCase().includes(s)
      )
    );
  }, [assets, q]);

  const removeAsset = async (id) => {
    try {
      await api.delete(`/assets/${id}`);
      toast.success("Asset deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const grouped = useMemo(() => {
    const m = {};
    for (const a of filtered) {
      const t = a.type || "other";
      if (!m[t]) m[t] = { total: 0, items: [] };
      m[t].total += Number(a.current_value || 0);
      m[t].items.push(a);
    }
    return Object.entries(m).sort((a, b) => b[1].total - a[1].total);
  }, [filtered]);

  return (
    <div className="space-y-8 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="label-kicker">Holdings</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A] mt-2">
            Assets
          </h1>
          <p className="text-[#5C635A] mt-2 max-w-xl">
            Bank accounts, investments, property, vehicles — anything that holds value. Update each
            value any time and the family net worth recalculates instantly.
          </p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white h-11 px-5 transition-all hover:-translate-y-0.5"
          data-testid="assets-add-asset"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add asset
        </Button>
      </div>

      <div className="card-soft p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="label-kicker">Total assets</p>
          <div className="font-display text-3xl font-medium num text-[#2C4A3B] mt-1" data-testid="assets-total">
            {formatEuro(total)}
          </div>
          <p className="text-xs text-[#8B9389] mt-1">{assets.length} item{assets.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B9389]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, owner, type…"
            className="pl-9 rounded-xl border-[#E5E3DB] bg-white"
            data-testid="assets-search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-[#8B9389] tracking-[0.2em] uppercase text-xs">Loading…</div>
      ) : assets.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <p className="label-kicker">Nothing here yet</p>
          <h3 className="font-display text-2xl text-[#1D3328] mt-2">Add your first asset</h3>
          <p className="text-[#5C635A] mt-2 max-w-md mx-auto">
            Even a single bank balance is a useful starting point — the dashboard will then show your
            family net worth.
          </p>
          <Button
            onClick={() => setAddOpen(true)}
            className="rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white mt-6"
            data-testid="assets-empty-add"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add asset
          </Button>
        </div>
      ) : (
        <div className="space-y-6 stagger">
          {grouped.map(([type, group]) => {
            const Icon = TYPE_ICON[type] || Box;
            const color = TYPE_COLOR[type] || "#5C635A";
            return (
              <section key={type} className="card-soft p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ background: `${color}18`, color }}
                    >
                      <Icon className="w-5 h-5" strokeWidth={1.7} />
                    </div>
                    <div>
                      <p className="label-kicker">{ASSET_TYPE_LABELS[type] || type}</p>
                      <div className="num text-lg font-medium text-[#1D3328]">
                        {formatEuro(group.total)}
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-[#8B9389]">{group.items.length} item{group.items.length !== 1 ? "s" : ""}</span>
                </div>
                <ul className="divide-y divide-[#EEEBE2]">
                  {group.items.map((a) => (
                    <li
                      key={a.id}
                      className="py-3 flex items-start justify-between gap-3 flex-wrap"
                      data-testid="asset-row"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-[#1D3328] truncate flex items-center gap-2">
                          {a.name}
                          {a.needs_refresh && (
                            <span
                              className="inline-flex items-center gap-1 rounded-md bg-[#FAEEE5] border border-[#E07A5F]/40 text-[#B04A32] text-[10px] font-medium px-1.5 py-0.5"
                              title={`Value not refreshed for ${a.days_since_update} days.`}
                              data-testid="asset-stale-badge"
                            >
                              <AlertCircle className="w-3 h-3" />
                              Refresh
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#8B9389] flex items-center gap-2 mt-0.5 flex-wrap">
                          {a.owner && <span>{a.owner}</span>}
                          {a.owner && <span className="text-[#C5CFC3]">·</span>}
                          <span>
                            Updated {a.last_value_update ? formatDate(a.last_value_update) : formatDate(a.updated_at)}
                            {a.days_since_update !== null && a.days_since_update !== undefined && (
                              <span className={a.needs_refresh ? "text-[#B04A32] font-medium" : ""}>
                                {" "}({relativeDays(a.days_since_update)})
                              </span>
                            )}
                          </span>
                        </div>
                        {a.notes && <p className="text-xs text-[#5C635A] mt-1 line-clamp-1">{a.notes}</p>}
                      </div>
                      <div className="text-right">
                        <div className="num text-lg font-medium text-[#1D3328]">{formatEuro(a.current_value)}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setValueTarget(a)}
                          className="inline-flex items-center gap-1 rounded-lg bg-[#EEEBE2] hover:bg-[#E5DCC5] text-[#1D3328] text-xs font-medium px-2.5 py-1.5 transition-colors"
                          data-testid="asset-update-value"
                          title="Update value"
                        >
                          <RefreshCw className="w-3.5 h-3.5" /> Update
                        </button>
                        <button
                          onClick={() => setEditTarget(a)}
                          className="inline-flex items-center rounded-lg text-[#5C635A] hover:text-[#1D3328] hover:bg-[#EEEBE2] px-2 py-1.5"
                          data-testid="asset-edit"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              className="inline-flex items-center rounded-lg text-[#B04A32] hover:bg-[#F7E6DF] px-2 py-1.5"
                              data-testid="asset-delete"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
                              <AlertDialogDescription>
                                The asset and all its value history will be removed. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => removeAsset(a.id)}
                                className="bg-[#E07A5F] hover:bg-[#C8624A]"
                                data-testid="asset-delete-confirm"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      <AssetForm open={addOpen} onOpenChange={setAddOpen} onSaved={() => load()} />
      <AssetForm
        open={Boolean(editTarget)}
        onOpenChange={(v) => { if (!v) setEditTarget(null); }}
        asset={editTarget}
        onSaved={() => load()}
      />
      <AssetValueDialog
        open={Boolean(valueTarget)}
        onOpenChange={(v) => { if (!v) setValueTarget(null); }}
        asset={valueTarget}
        onSaved={() => load()}
      />
    </div>
  );
}
